// @Flow
import fp from "lodash/fp"
import type { Dispatch } from "redux"

import checkFlightsApi from "../apis/checkFlightsApi"
import cuckoo from "common/services/cuckoo/cuckoo"
import { cuckooEvents } from "common/consts/enums"
import {
  shouldShowScheduleChangeModal,
  getScheduleChanges,
} from "booking/services/reservation/helpers/scheduleChangeHelper"
import { setFlightsChecked } from "../../states/actions"
import { updateAcceptedPricesConfig } from "../reservationReducerHelpers"
import passengersCountHash from "../passengersCountHash"
import { updateSuccess } from "../../actionCreators"
import updateUrl from "./updateUrl"
import { openModal } from "../../modals/actions"
import {
  checkFlightsParamsSelector,
  passengerCountersSelector,
} from "../selectors/reliantSelectors"
import { passengersValuesSelector } from "booking/services/reservation/selectors/passengersSelectors"
import { getAccepted, getHoldBagsPricingCurrent } from "../../prices/reducer"
import { getJourney } from "../../journey/reducer"
import { getLanguageInfo } from "common/services/staticData"
import { getBookingId, getBookingToken, getSubmitting, getMultistep } from "../../states/reducer"
import { isHiddenFlightReservationSelector } from "../selectors/reservationSelectors"
import { RESERVATION_MODALS } from "../../../scenes/Modals/consts/modalsConsts"
import { toStringLight } from "common/records/Journey/utils"
import updateAncillaries from "booking/services/reservation/actions/updateAncillaries"
import loadAncillaries3rd from "booking/services/ancillaries3rd/services/loadAncillaries3rd"
import priceChangeCheck from "./priceChangeCheck"

export const UPDATE_INTERVAL = 15000

const update = (options: any = {}) => (dispatch: Dispatch<any>, getState: () => State) => {
  const state = getState()
  const passengerCounter = passengerCountersSelector(state)
  const params = checkFlightsParamsSelector(state)()
  const holdBagsPricing = getHoldBagsPricingCurrent(state)
  const passengersCount = state.query.passengers
  const langInfo = getLanguageInfo(state)
  const useHiddenFlight = isHiddenFlightReservationSelector(state)
  const user = state.user.get("user")
  const affiliateId = user && user.affiliateId
  const { splitster } = state
  const isMultistep = getMultistep(state)
  const journey = getJourney(state)

  // UPDATE PRICES FOR 3rd ANCILLARIES
  if (options.passengersCountChange && isMultistep) {
    dispatch(loadAncillaries3rd())
  }
  // $FLowFixMe
  return checkFlightsApi({
    langInfo,
    params,
    options: state.options,
    useHiddenFlight,
    affiliateId,
    loadMissingPlacesNames: true,
    loadMissingPlacesNamesCacheOnly: true,
  })
    .then(res => {
      // CHECKS
      // ===

      // Already completed => ignore response
      // ---
      if (getBookingId(state)) {
        return
      }

      // Already submitting => check just `flightsChecked` part of response
      // BEWARE: It's bad logic, but quickest fix for most critical problem
      // updating only if `res.flightsChecked == true` is bad, but safest logic for quickfix
      // TODO: Rethink this whole logic
      // ---
      if (getSubmitting(state) && res.get("flightsChecked")) {
        dispatch(setFlightsChecked(res.get("flightsChecked")))
      }

      const usedPrice = priceChangeCheck({
        context: "update",
        res,
        splitster,
        originalPrice: getAccepted(state).get(passengersCountHash(passengerCounter)),
        passengersCount,
        dispatch,
        state,
      })

      if (holdBagsPricing && !holdBagsPricing.equals(res.get("holdBagsPricing"))) {
        cuckoo.track(cuckooEvents.BOOKING_PRICE_CHANGE_BAGS_PRICING, {
          old: holdBagsPricing.toJS(),
          new: res.get("holdBagsPricing").toJS(),
        })
      }

      if (res.get("journey").hasShortLayover()) {
        if (res.get("journey").hasNegativeLayover()) {
          cuckoo.track(cuckooEvents.BOOKING_JOURNEY_ITINERARY_NEGATIVE_LAYOVER, {
            location: window.location.href,
          })
        } else {
          cuckoo.track(cuckooEvents.BOOKING_JOURNEY_ITINERARY_SHORT_LAYOVER, {
            location: window.location.href,
          })
        }
        dispatch(openModal(RESERVATION_MODALS.ERROR.FAILURE, { passengersCount }))
      }

      if (getBookingToken(state) !== res.get("bookingToken")) {
        cuckoo.track(cuckooEvents.BOOKING_TOKEN_CHANGE, {
          from: getBookingToken(state),
          to: res.get("bookingToken"),
        })
      }

      const originalJourney = getJourney(state)
      const journeyDiff = originalJourney.getJourneyDiff(res.get("journey"))
      const scheduleChanges = getScheduleChanges(journeyDiff, originalJourney, res.get("journey"))

      if (shouldShowScheduleChangeModal(journeyDiff)) {
        dispatch(
          openModal(RESERVATION_MODALS.SCHEDULE_CHANGE, {
            journey: res.get("journey"),
            scheduleChanges,
          }),
        )
        cuckoo.track(cuckooEvents.SCHEDULE_CHANGE_MODAL_SHOWN)

        cuckoo.track(cuckooEvents.BOOKING_UPDATE_ITINERARY_CHANGE, {
          token: res.get("bookingToken"),
          diff: journeyDiff.toJS(),
          journeyOriginal: toStringLight(originalJourney),
          journeyUpdated: toStringLight(res.get("journey")),
        })
      }

      // update (accepted) prices in res
      const _res = res.set("basePrice", usedPrice).set(
        "accepted",
        updateAcceptedPricesConfig({
          origAccepted: getAccepted(state),
          passengers: passengerCounter,
          price: usedPrice,
        }),
      )

      const updateObject = {
        // States
        bookingToken: _res.get("bookingToken"),
        flightsChecked: _res.get("flightsChecked"),
        // Prices
        fees: _res.get("fees"),
        basePrice: _res.get("basePrice"),
        adultsPrice: _res.get("adultsPrice"),
        childrenPrice: _res.get("childrenPrice"),
        infantsPrice: _res.get("infantsPrice"),
        accepted: _res.get("accepted"),
      }

      dispatch(updateSuccess(fp.set("journey", _res.get("journey"), updateObject)))

      dispatch(updateUrl())

      updateAncillaries(state, dispatch, journey, passengersValuesSelector(state), state.options)
    })
    .catch(err => {
      cuckoo.warning(cuckooEvents.BOOKING_UPDATE_ERROR, {
        err,
        response: err.response,
      })
    })
}

export default update
